import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import FindTable from 'Components/Atoms/Icons/FindTable';
import useVisitWishReservation from 'CustomHooks/useVisitWishReservation';
import { conversionOf } from '../../Screens/VisitWishes/wishConversion';
import '../WaitinglistCard/styles.scss';
import type { ContextAction } from 'Components/Organisms/GTable';
import { VisitWish } from '../../Screens/VisitWishes/visitWishApi';
import { wishesOnDay } from '../../Screens/VisitWishes/wishFilters';
import WishStatus, { period, timeWindow } from '../../Screens/VisitWishes/WishStatus';

type Props = {
  restaurantId: string;
  date: string;
  wishes: VisitWish[];
  loading: boolean;
  failed: boolean;
  children?: React.ReactNode;
  otherRequestCount?: number;
  selectedWaitingId?: string | null;
  selectedId?: string | null;
  onOpen: (wish: VisitWish) => void;
  actions?: ContextAction<VisitWish>[];
  /** The wish whose action is still running; its buttons wait. */
  busyId?: string | null;
  /** Later open wishes exist that are not loaded yet; `onLoadMore` loads the next page. */
  hasMore?: boolean;
  onLoadMore?: () => void;
};

/** The calendar day's visit wishes, laid out like the waiting-list cards beside them. */
export default function VisitWishRequests({
  restaurantId,
  date,
  wishes,
  loading,
  failed,
  children,
  otherRequestCount = 0,
  selectedWaitingId,
  selectedId = null,
  onOpen,
  actions = [],
  busyId = null,
  hasMore = false,
  onLoadMore,
}: Props) {
  const { t } = useTranslation('reservations');
  const shown = wishesOnDay(wishes, date);
  const beginReservation = useVisitWishReservation(restaurantId);
  const container = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    if (!selectedWaitingId) return;
    const card = Array.from(container.current?.querySelectorAll<HTMLElement>('[data-waiting-entry]') ?? [])
      .find((element) => element.getAttribute('data-waiting-entry') === selectedWaitingId);
    card?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
  }, [selectedWaitingId, otherRequestCount]);
  return (
    <section ref={container} className="visit-wish-requests" aria-label={t('Requests')}>
      {loading ? (
        <p role="status" className="text-4 visit-wish-request-state">
          {t('Loading…')}
        </p>
      ) : null}
      {failed ? (
        <p role="alert" className="text-4 visit-wish-request-state">
          {t('Visit wishes could not be refreshed. Retrying automatically.')}
        </p>
      ) : null}
      {!loading && !failed && !hasMore && !shown.length && !otherRequestCount ? (
        <p className="text-4 visit-wish-request-state">{t('No requests for this day.')}</p>
      ) : null}
      {!loading && hasMore ? (
        <div className="visit-wish-request-state">
          <p className="text-4">{t('Not all open requests are loaded yet.')}</p>
          <Button variant="outlined" translation="reservations" onClick={onLoadMore}>
            Load more requests
          </Button>
        </div>
      ) : null}
      <ul className="visit-wish-request-list">
        {children}
        {shown.map((wish) => {
          const wishActions = actions.filter((action) => action.tooltipContent === 'Stop request' && (action.showCondition?.(wish) ?? true));
          const conversion = conversionOf(wish, date);
          return (
            <li
              key={wish.id}
              data-wish={wish.id}
              className={`waitinglist-card visit-wish-request-card${wish.id === selectedId ? ' active' : ''}`}
              onClick={() => onOpen(wish)}
            >
              <span className="text-5 request-type">{t('Visit wish')}</span>
              <span className="text-4 visit-wish-request-meta">
                {`${timeWindow(wish, t)} • ${wish.guests} Pax`}
              </span>
              <div className="visit-wish-request-main">
                <button
                  type="button"
                  className="visit-wish-request-open"
                  aria-current={wish.id === selectedId ? 'true' : undefined}
                >
                  <span className="text-3 weight-bold visit-wish-request-name">
                    {wish.contact.name}
                    {wish.vipTags?.length ? ' · VIP' : ''}
                  </span>
                </button>
                <span className="visit-wish-request-actions">
                  {wishActions.map((action) => {
                    const label = t(action.tooltipContent ?? '');
                    return (
                      <IconButton
                        key={action.tooltipContent}
                        size="small"
                        color="subdued"
                        aria-label={label}
                        title={label}
                        disabled={busyId === wish.id}
                        onClick={(event) => { event.stopPropagation(); action.onClick(wish); }}
                      >
                        {action.icon}
                      </IconButton>
                    );
                  })}
                  {conversion && (
                    <IconButton
                      size="small"
                      color="subdued"
                      aria-label={t(conversion.label)}
                      title={t(conversion.hint, conversion.values)}
                      disabled={busyId === wish.id || !conversion.possible}
                      onClick={(event) => { event.stopPropagation(); beginReservation(wish, date); }}
                    >
                      <FindTable className="text-subdued" />
                    </IconButton>
                  )}
                </span>
              </div>
              <span className="text-4 visit-wish-request-line">
                <WishStatus wish={wish} />
                {wish.dateFrom !== wish.dateTo ? <span className="visit-wish-request-meta">{period(wish)}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
